import { useMemo } from "react";

import type { Int32BTServiceGroupedListItemList } from "@btnet/services-btnet/handlers/models/int32BTServiceGroupedListItemList";

import { type AssignedUserExtraData, BTSelectItem } from "types/apiResponse/apiResponse";

import { BTSelectUser } from "commonComponents/btWrappers/BTSelectUser/BTSelectUser";

interface IPlanNotifyRecipientSelectProps {
    /**
     * The grouped notifiable-users list from the Plans GET response (Internal Users + Subs with job
     * access). Undefined while the plans data is still loading.
     */
    notifyUsers: Int32BTServiceGroupedListItemList | undefined;
    /** Currently selected recipient global user ids. */
    value: number[];
    onChange: (recipientGlobalUserIds: number[]) => void;
    disabled?: boolean;
    id: string;
    "data-testid"?: string;
}

/**
 * Multi-select people picker for the Plans notify flows (upload modal + revision notify modal). Wraps
 * BTSelectUser, sourcing its options from the server-provided grouped notify-users list so it stays in
 * sync with the user's job access. "Assign to me" is hidden — these are recipients, not an assignee.
 */
export function PlanNotifyRecipientSelect({
    notifyUsers,
    value,
    onChange,
    disabled,
    id,
    "data-testid": dataTestId,
}: Readonly<IPlanNotifyRecipientSelectProps>) {
    const treeData = useMemo(
        () =>
            (notifyUsers?.options ?? []).map(
                (option) => new BTSelectItem<AssignedUserExtraData>(option),
            ),
        [notifyUsers],
    );

    return (
        <BTSelectUser<undefined, AssignedUserExtraData>
            id={id}
            data-testid={dataTestId ?? id}
            treeData={treeData}
            value={value}
            multiple
            showAssignToMe={false}
            disabled={disabled}
            placeholder="Add recipients"
            onChange={(_field, newValue) => {
                // Multi-select hands back an array of ids; normalize to numbers and drop any
                // group-header sentinels that aren't real user ids.
                const ids = Array.isArray(newValue) ? newValue : newValue == null ? [] : [newValue];
                onChange(ids.map((idValue) => Number(idValue)).filter((n) => !Number.isNaN(n)));
            }}
            onBlur={() => {}}
        />
    );
}
